import React, { useState, useEffect } from "react";
import { all_routes } from "../../../router/all_routes";
import LeadDetailsSidebar from "../components/LeadDetailsSidebar";
import PageHeader from "../../../../core/common/crmComponents/PageHeader";
import LeadDetailsHeader from "../components/LeadDetailsHeader";
import LoaderComponent from "../../../../core/common/crmComponents/LoaderComponent";
import { useLocation, useParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchTimelineData } from "../../../../utils/helpers/apiHelpers";
import LeadPipelineStatus from "../components/LeadPipelineStatus";
import DisplayTimeline from "../../../../core/common/crmComponents/DisplayTimeline";
import { MODULES } from "../../../../core/data/constants/moduleConstants";
import { useDispatch, useSelector } from "react-redux";
import { setHasNewFilters } from "../../../../core/data/redux/filterSlice";
import { useCachedLeadData } from "../hooks/getCachedLeadData";
import queryClient from "../../../../config/queryClient";
import { setReloading } from "../../../../core/data/redux/moduleDataSlice";

const LeadsDetails = () => {
  const route = all_routes;
  const { id: leadId } = useParams();
  const dispatch = useDispatch();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const initialForceReload = searchParams.get("shouldRefetch") === "true";
  

  // const { forceReload: initialForceReload } = location.state || {};

  const [forceReload, setForceReload] = useState(false);
  const { leadData, isLoading, isError, error } = useCachedLeadData(
    leadId,
    null,
    initialForceReload || forceReload
  );

  const reloading = useSelector((state) => state.moduleData.reloading);

  const [view, setView] = useState("overview");

  useEffect(() => {
    dispatch(setHasNewFilters());
  }, []);

  useEffect(() => {
    if (reloading) {
      setForceReload(true);
      queryClient.invalidateQueries(["lead", leadId]);
      dispatch(setReloading(false));
    }
  }, [reloading, setForceReload]);

  useEffect(() => {
    if (forceReload) {
      setForceReload(false);
    }
  }, [forceReload]);

  // ✅ Fetch timeline data only when view is 'timeline'
  const {
    data: timelineData,
    isLoading: timelineLoading,
    isError: timelineError,
  } = useQuery({
    queryKey: ["timelineData", leadId],
    queryFn: () => fetchTimelineData(leadData?.timeline),
    enabled: !!leadData?.timeline && view === "timeline",
    staleTime: Infinity, // Keep the data fresh indefinitely
  });

  // ✅ Handle timeline error
  if (timelineError) return <div>Error loading timeline data.</div>;

  // ✅ Render dynamic content based on view
  const renderContent = () => {
    if (view === "overview") {
      return (
        <>
          <LeadPipelineStatus leadData={leadData} />
          <LeadDetailsSidebar leadData={leadData} />
        </>
      );
    } else if (view === "timeline") {
      return timelineLoading ? (
        <LoaderComponent />
      ) : (
        <>
          {timelineData && Object.keys(timelineData).length > 0 && (
            <DisplayTimeline
              timeline={timelineData}
              selectedData={leadData}
              moduleName={MODULES.LEADS}
            />
          )}
        </>
      );
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            <PageHeader pageName="Lead Overview" />
          </div>
        </div>
        <div className="row">
          {isLoading ? (
            <LoaderComponent />
          ) : isError ? (
            <div className="col-md-12">
              <div className="alert alert-danger text-capitalize" role="alert">
                {error?.response?.data?.msg}
              </div>
            </div>
          ) : (
            <LeadDetailsHeader
              leadData={leadData}
              view={view}
              setView={setView}
            />
          )}
        </div>
        <div className="row">{renderContent()}</div>
      </div>
    </div>
  );
};

export default LeadsDetails;
